import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const ROLES = [
  { icon: 'ri-music-2-line', label: 'Musicians', desc: 'Keys, Organ, Drums, Bass, Guitar' },
  { icon: 'ri-mic-line', label: 'Vocalists', desc: 'Lead & Background Singers' },
  { icon: 'ri-headphone-line', label: 'Audio Engineers', desc: 'FOH, Monitors, Recording' },
  { icon: 'ri-video-line', label: 'Media Teams', desc: 'Camera, Editing, Graphics' },
  { icon: 'ri-broadcast-line', label: 'Live Stream', desc: 'Technicians & Directors' },
  { icon: 'ri-user-star-line', label: 'Worship Leaders', desc: 'Ministers of Music & Directors' },
  { icon: 'ri-creative-commons-line', label: 'Creative Directors', desc: 'Vision, Branding & Production' },
  { icon: 'ri-group-line', label: 'Ministry Leadership', desc: 'Coordinators & Administrators' },
];

const PILLARS = [
  {
    icon: 'ri-user-heart-line',
    title: 'For Creatives',
    body: 'Submit your profile and we match you with churches and organizations that need your gift.',
    cta: 'Submit Your Info',
    to: '/ministry-connections?tab=creative',
    accent: 'from-brand-yellow/20 to-brand-yellow/5',
    border: 'border-brand-yellow/30',
    iconBg: 'bg-brand-yellow/20 text-brand-yellow',
    ctaStyle: 'bg-brand-yellow text-[#1C1917] hover:bg-yellow-300',
  },
  {
    icon: 'ri-community-line',
    title: 'For Churches & Orgs',
    body: 'Tell us the role, schedule, and budget — we find the right person and facilitate the connection.',
    cta: 'Submit Your Request',
    to: '/ministry-connections?tab=church',
    accent: 'from-white/10 to-white/5',
    border: 'border-white/15',
    iconBg: 'bg-white/10 text-white/80',
    ctaStyle: 'bg-white/10 text-white border border-white/25 hover:bg-white/20',
  },
];

export default function MinistryConnectionsBanner() {
  const { ref: heroRef, isVisible: heroVis } = useScrollAnimation({ threshold: 0.1 });
  const { ref: rolesRef, isVisible: rolesVis } = useScrollAnimation({ threshold: 0.1 });
  const { ref: pillarsRef, isVisible: pillarsVis } = useScrollAnimation({ threshold: 0.1 });
  const { ref: disclaimerRef, isVisible: disclaimerVis } = useScrollAnimation({ threshold: 0.2 });

  return (
    <section className="relative overflow-hidden bg-[#0F0E0C]">

      {/* ── Background image + overlays ── */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/9bbdf2d94f7d0ac7d93bca681379dc00.jpg"
          alt=""
          className="w-full h-full object-cover object-top opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F0E0C]/60 via-[#0F0E0C]/40 to-[#0F0E0C]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F0E0C]/80 via-transparent to-[#0F0E0C]/80" />
      </div>

      {/* Glow accents */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[300px] bg-brand-yellow/6 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-screen-xl mx-auto px-4 sm:px-8 py-28">

        {/* ── Hero headline ── */}
        <div ref={heroRef} className="text-center mb-20">
          <span
            className={`scroll-hidden-pop ${heroVis ? 'scroll-visible' : ''} inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-yellow/15 border border-brand-yellow/30 text-brand-yellow font-body text-xs font-bold uppercase tracking-widest mb-8`}
          >
            <i className="ri-gift-line" />
            100% Free Service · For the Glory of God
          </span>

          <h2
            className={`scroll-hidden ${heroVis ? 'scroll-visible' : ''} delay-100 font-heading text-5xl sm:text-6xl lg:text-8xl font-black text-white leading-tight mb-6`}
          >
            Ministry Staffing &amp;<br />
            <span className="text-brand-yellow">Creative Connections</span>
          </h2>

          <p
            className={`scroll-hidden-blur ${heroVis ? 'scroll-visible' : ''} delay-200 font-body text-white/60 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed mb-10`}
          >
            We connect creatives and ministry professionals with churches and organizations across music, media, production, and leadership — with excellence and integrity.
          </p>

          <div className={`scroll-hidden ${heroVis ? 'scroll-visible' : ''} delay-300 flex flex-wrap justify-center gap-4`}>
            <Link
              to="/ministry-connections"
              className="whitespace-nowrap inline-flex items-center gap-2 px-10 py-4 rounded-full bg-brand-yellow text-[#0F0E0C] font-body font-bold text-base hover:bg-yellow-300 transition-all cursor-pointer"
            >
              <i className="ri-links-line" />
              Explore This Service
            </Link>
            <a
              href="tel:9102424567"
              className="whitespace-nowrap inline-flex items-center gap-2 px-10 py-4 rounded-full border-2 border-white/25 text-white font-body font-semibold text-base hover:border-brand-yellow hover:text-brand-yellow transition-all cursor-pointer"
            >
              <i className="ri-phone-line" />
              910-242-4567
            </a>
          </div>
        </div>

        {/* ── Roles grid ── */}
        <div ref={rolesRef} className="mb-20">
          <p className={`scroll-hidden ${rolesVis ? 'scroll-visible' : ''} text-center font-body text-white/35 text-xs uppercase tracking-widest mb-8`}>
            Areas We Connect
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {ROLES.map((role, i) => (
              <div
                key={role.label}
                className={`scroll-hidden ${rolesVis ? 'scroll-visible' : ''} flex flex-col items-center text-center gap-3 p-5 rounded-2xl bg-white/5 border border-white/8 hover:bg-white/8 hover:border-brand-yellow/20 transition-all duration-300`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-brand-yellow/15 text-brand-yellow">
                  <i className={`${role.icon} text-xl`} />
                </div>
                <div>
                  <p className="font-body text-white font-bold text-sm leading-snug">{role.label}</p>
                  <p className="font-body text-white/40 text-xs leading-snug mt-0.5">{role.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Two-path pillars ── */}
        <div ref={pillarsRef} className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-16">
          {PILLARS.map((p, i) => (
            <div
              key={p.title}
              className={`scroll-hidden ${pillarsVis ? 'scroll-visible' : ''} bg-gradient-to-br ${p.accent} border ${p.border} rounded-3xl p-8 sm:p-10 flex flex-col gap-6`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${p.iconBg}`}>
                <i className={`${p.icon} text-2xl`} />
              </div>
              <div>
                <h3 className="font-heading text-white text-3xl font-black mb-3">{p.title}</h3>
                <p className="font-body text-white/55 text-base leading-relaxed">{p.body}</p>
              </div>
              <Link
                to={p.to}
                className={`whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full font-body font-bold text-sm transition-all cursor-pointer w-fit ${p.ctaStyle}`}
              >
                {p.cta} <i className="ri-arrow-right-line" />
              </Link>
            </div>
          ))}
        </div>

        {/* ── Disclaimer strip ── */}
        <div
          ref={disclaimerRef}
          className={`scroll-hidden-blur ${disclaimerVis ? 'scroll-visible' : ''} flex flex-col sm:flex-row items-center justify-center gap-3 text-center`}
        >
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-brand-yellow/15 text-brand-yellow flex-shrink-0">
            <i className="ri-information-line text-sm" />
          </div>
          <p className="font-body text-white/35 text-xs leading-relaxed max-w-2xl">
            Creative Institute LLC serves as a connection platform. We do not guarantee placement or employment. All final decisions and agreements are made directly between the parties involved. This service is provided free of charge to serve the community.
          </p>
        </div>

      </div>
    </section>
  );
}
